import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useAppSelector } from 'src/store';
import { useLoader } from 'src/provider/LoaderProvider';
import {
  useGetConfigurationSettingsPromptQuery,
  useGetCustomTemplatesQuery,
  useGetDefaultConfigurationTemplateFieldsQuery,
} from 'src/services';
import { Skeleton } from 'src/components/ui/skeleton';
import OnboardingStep4PrompBuilder from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep4/OnboardingStep4PrompBuilder/OnboardingStep4PrompBuilder';
import { OnboardingStep4Manual } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep4/OnboardingStep4Manual/OnboardingStep4Manual';
import type { ConfigurationSettings } from 'src/services/configurationService';
import type { PersonaInputData } from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';
import type {
  ConfigurationFormFieldsObject,
  IndexedFormField,
} from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import {
  toFormFieldsObject,
  withFieldIndex,
} from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import { usePagedConfigurationSettings } from 'src/pages/ConfigurationPage/hooks/usePagedConfigurationSettings';

const LoadingSkeleton = () => {
  return (
    <div className={'w-[455px] flex flex-col gap-5'}>
      <Skeleton className={'w-full h-4 mb-10'} />
      <Skeleton className={'w-full h-20'} />
      <Skeleton className={'w-full h-60'} />
    </div>
  );
};

export const OnboardingStep4 = () => {
  const { promptType, project } = useAppSelector((s) => s.onboardingSlice);
  const [activeSettings, setActiveSettings] = useState<ConfigurationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isManual, setIsManual] = useState(null);
  const [inputData, setInputData] = useState<PersonaInputData | null>(null);
  const [formFields, setFormFields] = useState<IndexedFormField[] | null>(null);
  const [formFieldsObject, setFormFieldsObject] = useState<ConfigurationFormFieldsObject>({});
  const loader = useLoader();
  const { data: defaultTemplateFields, isLoading: isLoadingDefaultTemplateFields } =
    useGetDefaultConfigurationTemplateFieldsQuery({}, { refetchOnMountOrArgChange: true });
  const { settings, isLoadingAllConfigurationSettings } = usePagedConfigurationSettings(
    project.id,
    { skip: !(project.id && defaultTemplateFields), refetchOnMountOrArgChange: true },
    null,
  );
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: project.id, settings_id: activeSettings?.id },
      {
        skip: !(project.id && activeSettings && defaultTemplateFields),
        refetchOnMountOrArgChange: true,
      },
    );
  const { data: customTemplatesData, isLoading: isLoadingCustomTemplates } =
    useGetCustomTemplatesQuery();

  useEffect(() => {
    if (
      isLoadingDefaultTemplateFields ||
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingCustomTemplates ||
      isManual === null ||
      settings === null
    ) {
      setIsLoading(true);
      loader.show();
    } else {
      setIsLoading(false);
      loader.hide();
    }
  }, [
    isLoadingAllConfigurationSettings,
    isLoadingDefaultTemplateFields,
    isLoadingConfigurationSettingsPrompt,
    isLoadingCustomTemplates,
    isManual,
    settings,
  ]);

  useEffect(() => {
    if (defaultTemplateFields) {
      if (!activeSettings && settings?.length) {
        setActiveSettings(settings[0]);
      }

      if (activeSettings) {
        const newActiveSettings = settings.find((item) => {
          return item.id === activeSettings.id;
        });

        setActiveSettings(newActiveSettings);
      } else {
        const fields = withFieldIndex(defaultTemplateFields.form_fields);
        setFormFields(fields);
        setInputData(null);
      }
    }
  }, [settings, defaultTemplateFields]);

  useEffect(() => {
    if (formFields) {
      const fieldsObject = toFormFieldsObject(formFields);

      setFormFieldsObject(fieldsObject);
    }
  }, [formFields]);

  useEffect(() => {
    if (configurationSettingsPrompt && !configurationSettingsPrompt.is_manual) {
      const fields = withFieldIndex(configurationSettingsPrompt.form_fields);

      setFormFields(fields);
      setInputData(configurationSettingsPrompt.input_data);
    } else if (defaultTemplateFields) {
      const fields = withFieldIndex(defaultTemplateFields.form_fields);
      setFormFields(fields);
      setInputData(null);
    }
  }, [configurationSettingsPrompt, defaultTemplateFields]);

  useEffect(() => {
    if (promptType) {
      setIsManual(promptType === 'manual');
    }
  }, [promptType]);

  return (
    <div className={'flex flex-col items-center w-full max-w-[550px]'}>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <div className={clsx('flex flex-col w-full', !isManual && 'max-w-[455px]')}>
          {isManual ? (
            <OnboardingStep4Manual
              customTemplatesData={customTemplatesData}
              configurationSettingsPrompt={configurationSettingsPrompt}
            />
          ) : (
            <OnboardingStep4PrompBuilder
              activeSettings={activeSettings}
              inputData={inputData}
              formFieldsObject={formFieldsObject}
              defaultTemplateFields={defaultTemplateFields}
            />
          )}
        </div>
      )}
    </div>
  );
};
